import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, ShieldCheck, ShieldQuestion } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import LoadFailureNote from "../components/LoadFailureNote";
import { fetchAdminGuideList } from "../lib/guides";

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-NZ");
}

/**
 * The reviewer's working checklist (spec §2.2 point 4, PR #191 Martin's
 * answer 4: "build it now"). Deliberately smaller than `/admin/guides`
 * (issue #219's full console, with filters and Publish/Unpublish): this is
 * "12 raw links" made bearable, one row per guide with a direct hop into
 * `?review=1` — force-expanded, ready to sign off. The publish/unpublish
 * ACTIONS live on the console and on the guide page itself; this page only
 * ever links onward.
 */
export default function LearnReview() {
  const [guides, setGuides] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchAdminGuideList()
      .then((rows) => {
        if (cancelled) return;
        setGuides(rows);
        setLoadFailed(false);
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) { setLoadFailed(true); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const reviewedCount = guides.filter((g) => g.state === "published").length;

  return (
    <div className="space-y-6" data-testid="learn-review-page">
      <div className="relative overflow-hidden rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg, #0B2E59 0%, #15417A 100%)" }}>
        <p className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "#D4A017" }}>Reviewer checklist</p>
        <h1 className="font-display font-black text-2xl mt-1">Walk every guide before it ships</h1>
        <p className="text-white/70 mt-1 text-sm">
          {loaded && !loadFailed ? `${reviewedCount} of ${guides.length} reviewed` : "Every imported guide, in order."}
        </p>
        <ClipboardCheck size={64} className="absolute right-4 top-4 text-white/10 hidden sm:block" />
      </div>

      {loadFailed ? (
        <Card className="border-slate-200">
          <CardContent className="p-10 flex">
            <LoadFailureNote what="the checklist" onRetry={() => setReloadKey((k) => k + 1)} testId="learn-review-failed" />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="learn-review-list">
          {guides.map((g) => {
            const published = g.state === "published";
            return (
              <Link key={`${g.category}/${g.slug}`} to={`/learn/${g.category}/${g.slug}?review=1`} className="block">
                <Card
                  className="border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all"
                  data-testid={`learn-review-row-${g.category}-${g.slug}`}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    {published ? (
                      <ShieldCheck size={18} className="text-emerald-600 shrink-0" />
                    ) : (
                      <ShieldQuestion size={18} className="text-amber-600 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 break-words">{g.title}</span>
                        <Badge variant="outline" className="uppercase text-[10px]">{g.category.replace(/-/g, " ")}</Badge>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {published ? `Reviewed by ${g.reviewer_name} · ${fmtWhen(g.reviewed_at)}` : "Not yet reviewed"}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
